
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { XCircle } from "lucide-react";
import { Agendamento } from "@/hooks/useAgendamentos";

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'AGENDADO':
      return <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-green-500/10 text-green-500 border border-green-500/40 uppercase tracking-tight whitespace-nowrap"><span className="w-2 h-2 rounded-full bg-green-500 mr-2 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>AGENDADO</span>;
    case 'CANCELADO':
      return <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/40 uppercase tracking-tight whitespace-nowrap"><span className="w-2 h-2 rounded-full bg-red-500 mr-2 shadow-[0_0_8px_rgba(239,68,68,0.6)]"></span>CANCELADO</span>;
    case 'REAGENDADO':
      return <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-orange-500/10 text-orange-500 border border-orange-500/40 uppercase tracking-tight whitespace-nowrap"><span className="w-2 h-2 rounded-full bg-orange-500 mr-2 shadow-[0_0_8px_rgba(249,115,22,0.6)]"></span>REAGENDADO</span>;
    default:
      return <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/40 uppercase tracking-tight whitespace-nowrap"><span className="w-2 h-2 rounded-full bg-gray-500 mr-2"></span>{status}</span>;
  }
};

interface CancelamentosDiaProps {
  agendamentos: Agendamento[];
  isLoading: boolean;
}

export const CancelamentosDia = ({ agendamentos, isLoading }: CancelamentosDiaProps) => {
  const formatDate = (dateString: string) => {
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('pt-BR');
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-2xl font-semibold text-card-foreground flex items-center gap-2">
            <XCircle className="w-6 h-6 text-primary" />
            CANCELAMENTOS
          </CardTitle>
          <CardDescription className="text-muted-foreground text-lg flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Total: {agendamentos.length} cancelamentos do dia
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-medium text-xs md:text-base">Cliente</TableHead>
                <TableHead className="text-muted-foreground font-medium text-xs md:text-base">Data</TableHead>
                <TableHead className="text-muted-foreground font-medium text-xs md:text-base">Hora</TableHead>
                <TableHead className="text-muted-foreground font-medium text-xs md:text-base">Status</TableHead>
                <TableHead className="text-muted-foreground font-medium text-xs md:text-base">Profissional</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agendamentos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-lg">
                    Nenhum cancelamento hoje
                  </TableCell>
                </TableRow>
              ) : (
                agendamentos.map((agendamento) => (
                  <TableRow key={agendamento.id} className="border-border hover:bg-muted/30">
                    <TableCell className="font-medium text-card-foreground text-sm md:text-lg">{agendamento.NOME}</TableCell>
                    <TableCell className="text-muted-foreground text-xs md:text-base">{formatDate(agendamento.DATA)}</TableCell>
                    <TableCell className="text-muted-foreground text-xs md:text-base font-bold">{formatTime(agendamento.HORA)}</TableCell>
                    <TableCell>{getStatusBadge(agendamento.STATUS)}</TableCell>
                    <TableCell className="text-muted-foreground text-xs md:text-base">{agendamento.PROFISSIONAL}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
